import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { checkUserPermission } from '../../../hooks/useHasPermission';
import {
  CLOSING_PACKAGE_PERMISSIONS,
  userHasClosingPackageRole,
} from '../permissions/closingPackagePermissions';

/**
 * Permission / role gates for Closing Package Action Bar (FE-005+).
 * Backend remains authoritative; FE gates are UX-only.
 */
export function useClosingPackagePermissions() {
  const user = useSelector((state: RootState) => state.auth.user);

  return useMemo(() => {
    const canView = checkUserPermission(user, CLOSING_PACKAGE_PERMISSIONS.view);
    const canUpload = checkUserPermission(user, CLOSING_PACKAGE_PERMISSIONS.upload);
    const canEdit = checkUserPermission(user, CLOSING_PACKAGE_PERMISSIONS.edit);
    const canDispatch = checkUserPermission(user, CLOSING_PACKAGE_PERMISSIONS.dispatch);

    const isAdminOrAccountant = userHasClosingPackageRole(user, ['admin', 'accountant']);
    const isAdminOrLogist = userHasClosingPackageRole(user, ['admin', 'logist']);

    return {
      canView,
      canUpload,
      canEdit,
      canDispatch,
      canConfirmFormation: canEdit && isAdminOrAccountant,
      canDispatchScans: canDispatch && isAdminOrLogist,
      canRegisterApproval: canDispatch && isAdminOrLogist,
      canPrepareOriginals: canEdit && isAdminOrAccountant,
      canDispatchOriginals: canEdit && isAdminOrAccountant,
      canConfirmOriginalsReceipt: canDispatch && isAdminOrLogist,
    };
  }, [user]);
}
